import React, { useState, useEffect, useCallback } from 'react';
import AdminDashboard from './AdminDashboard';
import UsersList from './UsersList';
import UserDetail from './UserDetail';
import TransactionsList from './TransactionsList';
import LedgerView from './LedgerView';
import ReferralsView from './ReferralsView';
import InvoicesView from './InvoicesView';
import TaxOverview from './TaxOverview';
import PackageManagement from './PackageManagement';
import BonusLinksManagement from './BonusLinksManagement';
import PriceListManagement from './PriceListManagement';
import PriceListAdmin from './PriceListAdmin';
import ReferralTextsEditor from './ReferralTextsEditor';
import LegalTextsAdmin from './LegalTextsAdmin';
import SystemAuditHistory from './SystemAuditHistory';
import DsgvoDeleteLog from './DsgvoDeleteLog';
import AstriaTuningSettings from './AstriaTuningSettings';
import ModiSettings from './ModiSettings';
import MaintenanceToggle from './MaintenanceToggle';  // NEU: Wartungsmodus
import VideoTemplatesAdmin from './VideoTemplatesAdmin';
import VideoModelsAdmin from './VideoModelsAdmin';
import VideoPlayground from './VideoPlayground';  // NEU: Video-Templates (Phase 2)
import ApiKeysAdmin from './ApiKeysAdmin';  // NEU: API Keys Management
import SystemDashboard from './SystemDashboard';  // NEU: Command Center
import CodeLibrary from './CodeLibrary';  // NEU: Code-Bibliothek
import CinemaSettings from './CinemaSettings';  // NEU: Kino-Modus
import SocialMediaManager from './SocialMediaManager';  // NEU: Social Media
import TriggerWordsAdmin from './TriggerWordsAdmin';  // NEU: Trigger Words & Prompt Keywords
import SeoKeywordsAdmin from './SeoKeywordsAdmin';  // NEU: SEO Keywords Management
import VideoLogAdmin from './VideoLogAdmin';  // NEU: Video Log
import PhotoLogAdmin from './PhotoLogAdmin';  // NEU: Foto Log
import StudioAdmin from './StudioAdmin';  // Drehbuch + Music Analyzer
import OnboardingAdmin from './OnboardingAdmin';
import { LogOut, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../../config';

// Gültige Admin-Views für URL-Validierung
const VALID_VIEWS = new Set([
  'dashboard', 'users', 'transactions', 'ledger', 'referrals', 'referral-texts',
  'invoices', 'tax-overview', 'packages', 'bonus-links', 'price-list', 'snap-costs',
  'video-templates', 'video-models', 'video-playground', 'video-log', 'photo-log',
  'api-keys', 'legal-texts', 'system-audit', 'dsgvo-log', 'astria-tuning',
  'modi-settings', 'trigger-words', 'seo-keywords', 'cinema-settings', 'social-media',
  'maintenance', 'code-library', 'system', 'screenplay', 'onboarding'
]);

export default function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();

  // View aus URL ableiten: /admin/users → 'users', /admin → 'dashboard'
  const getViewFromPath = useCallback(() => {
    const segments = location.pathname.replace(/\/+$/, '').split('/');
    // /admin/view-name → segments = ['', 'admin', 'view-name']
    const viewSegment = segments[2] || 'dashboard';
    return VALID_VIEWS.has(viewSegment) ? viewSegment : 'dashboard';
  }, [location.pathname]);

  const currentView = getViewFromPath();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Reset selectedUserId wenn View wechselt (z.B. durch Browser-Back)
  useEffect(() => {
    setSelectedUserId(null);
  }, [currentView]);

  // Blockiere body scroll wenn Hamburger-Menü offen ist
  useEffect(() => {
    if (menuOpen) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }

    // Cleanup beim Unmount
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [menuOpen]);

  // Prüfe ob User eingeloggt ist
  useEffect(() => {
    const token = localStorage.getItem('snapshot_token');
    if (!token) {
      navigate('/');
    }
  }, [navigate]);

  const handleLogout = () => {
    navigate('/');
  };
  
  // Helper: Admin-Daten aus localStorage holen
  const getAdminData = () => {
    const snapshot_user = localStorage.getItem('snapshot_user');
    const snapshot_token = localStorage.getItem('snapshot_token');
    if (snapshot_user && snapshot_token) {
      try {
        const user = JSON.parse(snapshot_user);
        return { email: user.email, role: 'admin', token: snapshot_token };
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  
  const adminData = getAdminData();

  const handleViewChange = (view) => {
    const path = view === 'dashboard' ? '/admin' : `/admin/${view}`;
    navigate(path);
    localStorage.setItem('admin_current_view', view); // Persist für Fallback
    setSelectedUserId(null); // Reset user detail when changing views
  };
  
  // Helper: View-Label für Mobile-Dropdown
  const getViewLabel = (view) => {
    const labels = {
      'dashboard': 'Dashboard',
      'users': 'Nutzer',
      'transactions': 'Transactions',
      'ledger': 'Ledger',
      'referrals': 'Referrals',
      'referral-texts': 'Referral Texte',
      'invoices': 'Rechnungen',
      'tax-overview': 'Steuer',
      'packages': 'Pakete',
      'bonus-links': 'Bonus & Links',
      'price-list': 'Preisliste',
      'snap-costs': 'Backend-Kosten',
      'video-templates': 'Video-Templates',
      'video-log': 'Video Log',
      'photo-log': 'Foto Log',
      'onboarding': 'Onboarding',
      'api-keys': 'API Keys',
      'astria-tuning': 'Astria Tuning',
      'modi-settings': 'Modi Settings',
      'maintenance': 'Wartungsmodus',
      'cinema-settings': 'Kino-Modus',
      'social-media': 'Social Media',
      'trigger-words': 'Trigger Words',
      'seo-keywords': 'SEO Keywords',
      'code-library': '💻 Code-Bibliothek',
      'system': '🏗️ System',
      'screenplay': '🎬 Drehbuch & Musik',
    };
    return labels[view] || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-black grain-texture flex" style={{ overscrollBehavior: 'none' }}>
      
      {/* Desktop Sidebar - Permanent (nur lg:) */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:left-0 lg:top-0 lg:h-screen lg:bg-black/95 lg:backdrop-blur-xl lg:border-r lg:border-white/10 lg:z-50">
        {/* Sidebar Header */}
        <div className="p-6 border-b border-white/10">
          <h1 className="text-xl font-bold text-white">
            SNAPSHOT <span className="text-[#10B981]">Admin</span>
          </h1>
          <p className="text-xs text-white/50 mt-1">{adminData?.email}</p>
        </div>
        
        {/* Sidebar Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {/* Zurück zur ID-Card — immer sichtbar, damit man jederzeit aus dem
              Admin in die normale Oberfläche zurückkommt (auch ohne Master-Rolle). */}
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-3 text-left font-semibold rounded-xl transition-all border-2 border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 flex items-center gap-3 mb-4"
            data-testid="back-to-id-card-desktop"
          >
            <ArrowLeft className="w-5 h-5" />
            Zurück zur Oberfläche
          </button>
          
          {[
            {id: 'dashboard', label: 'Dashboard', icon: '📊'},
            {id: 'onboarding', label: 'Onboarding', icon: '🧭'},
            {id: 'users', label: 'Nutzer', icon: '👥'},
            {id: 'transactions', label: 'Transactions', icon: '💳'},
            {id: 'ledger', label: 'Ledger', icon: '📒'},
            {id: 'referrals', label: 'Referrals', icon: '🤝'},
            {id: 'referral-texts', label: 'Referral Texte', icon: '📝'},
            {id: 'invoices', label: 'Rechnungen', icon: '🧾'},
            {id: 'tax-overview', label: 'Steuer', icon: '📋'},
            {id: 'packages', label: 'Pakete', icon: '📦'},
            {id: 'bonus-links', label: 'Bonus & Links', icon: '🎁'},
            {id: 'price-list', label: 'Preisliste', icon: '💰'},
            {id: 'snap-costs', label: 'Backend-Kosten', icon: '💎'},
            {id: 'screenplay', label: 'Drehbuch & Musik', icon: '🎬'},
            {id: 'video-templates', label: 'Video-Templates', icon: '🎬'},
            {id: 'video-models', label: 'Video Modelle', icon: '🤖'},
            {id: 'video-playground', label: 'Playground', icon: '🎮'},
            {id: 'video-log', label: 'Video Log', icon: '📹'},
            {id: 'photo-log', label: 'Foto Log', icon: '📸'},
            {id: 'api-keys', label: 'API Keys', icon: '🔑'},
            {id: 'legal-texts', label: 'Rechtstexte', icon: '⚖️'},
            {id: 'system-audit', label: 'System-Historie', icon: '📜'},
            {id: 'dsgvo-log', label: 'DSGVO Löschungen', icon: '🛡️'},
            {id: 'astria-tuning', label: 'Astria Tuning', icon: '⚙️'},
            {id: 'modi-settings', label: 'Modi Settings', icon: '🎨'},
            {id: 'trigger-words', label: 'Trigger Words', icon: '🛡️'},
            {id: 'seo-keywords', label: 'SEO Keywords', icon: '🌐'},
            {id: 'cinema-settings', label: 'Kino-Modus', icon: '🎬'},
            {id: 'social-media', label: 'Social Media', icon: '📱'},
            {id: 'maintenance', label: 'Wartungsmodus', icon: '🔧'},
            {id: 'code-library', label: 'Code-Bibliothek', icon: '💻'},
            {id: 'system', label: 'System', icon: '🏗️'},
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => handleViewChange(item.id)}
              className={`w-full px-4 py-3 text-left font-medium rounded-xl transition-all flex items-center gap-3 ${
                currentView === item.id
                  ? 'bg-[#10B981] text-black'
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`sidebar-${item.id}`}
            >
              <span className="text-lg">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        
        {/* Sidebar Footer */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 font-semibold rounded-xl transition-all flex items-center gap-2 justify-center"
            data-testid="admin-logout-sidebar"
          >
            <LogOut className="w-5 h-5" />
            Abmelden
          </button>
        </div>
      </aside>
      
      {/* Main Content - mit Margin für Desktop Sidebar */}
      <div className="flex-1 lg:ml-64 w-full min-w-0">
        {/* Top Navigation - Nur für Mobile/Tablet */}
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40" style={{ touchAction: 'pan-y' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            {/* Hamburger Button - nur Mobile */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              data-testid="admin-mobile-hamburger"
              className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg transition-all touch-manipulation"
              style={{ touchAction: 'manipulation' }}
              aria-label="Menü öffnen"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <h1 className="text-lg md:text-xl font-bold text-white">
              SNAPSHOT <span className="text-[#10B981]">Admin</span>
            </h1>
            
            {/* Current View Label */}
            <div className="hidden sm:block px-3 py-1 bg-[#10B981]/20 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm font-medium">
              {getViewLabel(currentView)}
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile: Logout */}
            <div className="lg:hidden flex gap-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-white/60 hover:text-red-500 transition-all"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          </div>
        </div>
        </div>

        {/* Full-Screen Mobile Menu Overlay - AUSSERHALB der Top-Navigation */}
        {menuOpen && (
          <>
            {/* Overlay zum Schließen */}
            <div 
              className="lg:hidden fixed inset-0 bg-black/50 z-[100]"
              onClick={() => setMenuOpen(false)}
            ></div>
            
            {/* Full-Screen Mobile Menu */}
            <div className="lg:hidden fixed inset-0 bg-black/98 backdrop-blur-xl z-[110] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/20 flex-shrink-0">
                <h2 className="text-xl font-bold text-white">Menü</h2>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all"
                  aria-label="Menü schließen"
                >
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Menu Items - mit Scroll */}
              <div className="flex-1 overflow-y-auto overscroll-contain p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Zurück zur Oberfläche — immer sichtbar */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/');
                  setMenuOpen(false);
                }}
                className="w-full px-6 py-4 text-left font-bold transition-all mb-4 border-2 border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 flex items-center gap-3 rounded-xl touch-manipulation"
                data-testid="back-to-id-card-mobile"
              >
                <ArrowLeft className="w-5 h-5" />
                Zurück zur Oberfläche
              </button>
              
              <div className="space-y-2">
              {[
                {id: 'dashboard', label: 'Dashboard'},
                {id: 'onboarding', label: 'Onboarding'},
                {id: 'users', label: 'Nutzer'},
                {id: 'transactions', label: 'Transactions'},
                {id: 'ledger', label: 'Ledger'},
                {id: 'referrals', label: 'Referrals'},
                {id: 'referral-texts', label: 'Referral Texte'},
                {id: 'invoices', label: 'Rechnungen'},
                {id: 'tax-overview', label: 'Steuer'},
                {id: 'packages', label: 'Pakete'},
                {id: 'bonus-links', label: 'Bonus & Links'},
                {id: 'price-list', label: 'Preisliste'},
                {id: 'snap-costs', label: 'Backend-Kosten'},
                {id: 'screenplay', label: '🎬 Drehbuch & Musik'},
                {id: 'video-templates', label: 'Video-Templates'},
                {id: 'video-log', label: 'Video Log'},
                {id: 'api-keys', label: 'API Keys'},
                {id: 'legal-texts', label: 'Rechtstexte'},
                {id: 'system-audit', label: 'System-Historie'},
                {id: 'dsgvo-log', label: 'DSGVO Löschungen'},
                {id: 'astria-tuning', label: 'Astria Tuning'},
                {id: 'modi-settings', label: 'Modi Settings'},
                {id: 'trigger-words', label: 'Trigger Words'},
                {id: 'seo-keywords', label: 'SEO Keywords'},
                {id: 'cinema-settings', label: 'Kino-Modus'},
                {id: 'social-media', label: 'Social Media'},
                {id: 'maintenance', label: 'Wartungsmodus'},
                {id: 'code-library', label: '💻 Code-Bibliothek'},
                {id: 'system', label: '🏗️ System'},
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewChange(item.id);
                    setMenuOpen(false);
                  }}
                  className={`w-full px-6 py-4 text-left font-medium text-lg transition-all rounded-xl touch-manipulation ${
                    currentView === item.id
                      ? 'bg-[#10B981] text-black font-bold'
                      : 'text-white/80 hover:bg-white/10 hover:text-white bg-white/5'
                  }`}
                  data-testid={`mobile-menu-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
              </div>
              </div>
            </div>
          </>
        )}

        {/* Content - mit padding (reduziert für Mobile) */}
        <div className="p-2 sm:p-4 lg:p-6 w-full overflow-x-hidden">
        {currentView === 'dashboard' && <AdminDashboard adminData={adminData} onViewChange={handleViewChange} />}
        {currentView === 'onboarding' && <OnboardingAdmin adminToken={adminData.token} />}
        {currentView === 'users' && !selectedUserId && (
          <UsersList 
            adminData={adminData} 
            onUserClick={(userId) => setSelectedUserId(userId)}
          />
        )}
        {currentView === 'users' && selectedUserId && (
          <UserDetail 
            userId={selectedUserId}
            onBack={() => setSelectedUserId(null)}
          />
        )}
        {currentView === 'transactions' && <TransactionsList adminData={adminData} />}
        {currentView === 'ledger' && <LedgerView adminData={adminData} />}
        {currentView === 'referrals' && <ReferralsView adminData={adminData} />}
        {currentView === 'referral-texts' && <ReferralTextsEditor />}
        {currentView === 'invoices' && <InvoicesView adminData={adminData} />}
        {currentView === 'tax-overview' && <TaxOverview adminData={adminData} />}
        {currentView === 'packages' && <PackageManagement adminToken={adminData.token} />}
        {currentView === 'bonus-links' && <BonusLinksManagement />}
        {currentView === 'price-list' && <PriceListManagement adminToken={adminData.token} />}
        {currentView === 'snap-costs' && <PriceListAdmin />}
        {currentView === 'video-templates' && <VideoTemplatesAdmin />}
        {currentView === 'video-models' && <VideoModelsAdmin />}
        {currentView === 'video-playground' && <VideoPlayground />}
        {currentView === 'video-log' && <VideoLogAdmin />}
        {currentView === 'photo-log' && <PhotoLogAdmin />}
        {currentView === 'api-keys' && <ApiKeysAdmin />}
        {currentView === 'legal-texts' && <LegalTextsAdmin />}
        {currentView === 'system-audit' && <SystemAuditHistory />}
        {currentView === 'dsgvo-log' && <DsgvoDeleteLog />}
        {currentView === 'astria-tuning' && <AstriaTuningSettings adminToken={adminData.token} />}
        {currentView === 'modi-settings' && <ModiSettings adminToken={adminData.token} />}
        {currentView === 'trigger-words' && <TriggerWordsAdmin />}
        {currentView === 'seo-keywords' && <SeoKeywordsAdmin />}
        {currentView === 'cinema-settings' && <CinemaSettings adminToken={adminData.token} />}
        {currentView === 'social-media' && <SocialMediaManager adminToken={adminData.token} />}
        {currentView === 'maintenance' && <MaintenanceToggle />}
        {currentView === 'code-library' && <CodeLibrary />}
        {currentView === 'system' && <SystemDashboard />}
        {currentView === 'screenplay' && <StudioAdmin adminToken={adminData.token} />}
        </div>
        
      </div>

    </div>
  );
}
